import { useEffect, useRef, useCallback } from 'react';
import api from '../services/api';

const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'click',
  'scroll',
  'touchstart',
];

/**
 * Hook to track user activity and update lastInteractionTime on the backend.
 * Call this hook on pages where attendance is being tracked.
 * 
 * @param {Object} options
 * @param {string} options.attendanceId - The attendance record ID to update
 * @param {string} options.employeeId - The employee ID (optional, will use current user)
 * @param {number} options.updateInterval - Minimum interval between API calls in ms (default: 30000)
 * @param {boolean} options.enabled - Whether tracking is enabled
 */
export const useActivityTracker = ({ attendanceId, employeeId, updateInterval = 30000, enabled = true } = {}) => {
  const lastUpdateRef = useRef(0);
  const isActiveRef = useRef(false);
  const activityTimeoutRef = useRef(null);

  const updateActivity = useCallback(async () => {
    if (!enabled || !attendanceId) {
      console.log('[ActivityTracker] Skipped - enabled:', enabled, 'attendanceId:', attendanceId);
      return;
    }

    const now = Date.now();
    console.log('[ActivityTracker] Activity detected, checking interval...', {
      timeSinceLastUpdate: now - lastUpdateRef.current,
      updateInterval,
    });

    if (now - lastUpdateRef.current < updateInterval) {
      console.log('[ActivityTracker] Skipped - too soon since last update');
      return;
    }

    lastUpdateRef.current = now;
    const timestamp = new Date().toISOString();
    console.log('[ActivityTracker] Sending update to backend:', { attendanceId, employeeId, lastInteractionTime: timestamp });

    try {
      const response = await api.post('/training/work-attendance/activity/update', {
        attendanceId,
        employeeId,
        lastInteractionTime: timestamp,
      });
      console.log('[ActivityTracker] Update successful:', response.data);
    } catch (error) {
      console.error('[ActivityTracker] Failed to update activity:', error);
    }
  }, [attendanceId, employeeId, updateInterval, enabled]);

  useEffect(() => {
    console.log('[ActivityTracker] Hook initialized - enabled:', enabled, 'attendanceId:', attendanceId);

    if (!enabled || !attendanceId) {
      console.log('[ActivityTracker] Hook not active - conditions not met');
      return;
    }

    const handleActivity = () => {
      if (!isActiveRef.current) {
        isActiveRef.current = true;
        updateActivity();
      }
      
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
      
      activityTimeoutRef.current = setTimeout(() => {
        isActiveRef.current = false;
      }, 5000);
    };

    ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    window.addEventListener('focus', updateActivity);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        updateActivity();
      }
    });

    return () => {
      ACTIVITY_EVENTS.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      window.removeEventListener('focus', updateActivity);
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
    };
  }, [enabled, attendanceId, updateActivity]);

  return { updateActivity };
};

export default useActivityTracker;
